/*
  brightly.js — shared behavior layer for the Brightly (Shrine-Pro-style) rebuild.
  Sections stay pure markup + CSS + schema and hook into these contracts via
  class names / data-attributes. Loaded once, globally, from theme.liquid.

  Contracts
  ---------
  .animate-section                  -> fades/blurs in when scrolled into view (adds .is-visible)
  [data-ring][data-percent]         -> animates CSS var --p 0..percent; child [data-ring-number] counts up
  [data-countdown][data-duration]   -> MM:SS countdown (seconds); writes into [data-countdown-display] or self
  [data-copy-code]                  -> click copies attr value; swaps label to data-copied-label (or "Копирано!")
  .brightly-scroll-top              -> shows after 400px scroll; click scrolls to top
  [data-promo-popup]                -> shows after data-delay s (default 5); 30-day dismissal in localStorage
  [data-bundle]                     -> .bundle-option children select; updates [data-bundle-atc-price] + hidden input
  .brightly-sticky-atc              -> shows when [data-sticky-atc-trigger] leaves the viewport
*/
(function () {
  'use strict';

  var prefersReduced =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Scroll reveal ---------- */
  function initReveal(root) {
    var els = (root || document).querySelectorAll('.animate-section:not(.is-bound)');
    if (!('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-visible', 'is-bound'); });
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    els.forEach(function (el) { el.classList.add('is-bound'); io.observe(el); });
  }

  /* ---------- Percentage rings ---------- */
  function animateRing(el) {
    var pct = parseFloat(el.getAttribute('data-percent')) || 0;
    var num = el.querySelector('[data-ring-number]');
    if (prefersReduced) {
      el.style.setProperty('--p', pct);
      if (num) num.textContent = Math.round(pct) + '%';
      return;
    }
    var start = null;
    var dur = 1500;
    function step(ts) {
      if (start === null) start = ts;
      var t = Math.min((ts - start) / dur, 1);
      var eased = 1 - Math.pow(1 - t, 3);
      var val = pct * eased;
      el.style.setProperty('--p', val);
      if (num) num.textContent = Math.round(val) + '%';
      if (t < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }
  function initRings(root) {
    var rings = (root || document).querySelectorAll('[data-ring]:not(.is-bound)');
    if (!('IntersectionObserver' in window)) {
      rings.forEach(function (r) { r.classList.add('is-bound'); animateRing(r); });
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) { animateRing(entry.target); io.unobserve(entry.target); }
        });
      },
      { threshold: 0.4 }
    );
    rings.forEach(function (r) { r.classList.add('is-bound'); io.observe(r); });
  }

  /* ---------- Countdown timers ---------- */
  function fmt(s) {
    var m = Math.floor(s / 60);
    var sec = s % 60;
    return (m < 10 ? '0' : '') + m + ':' + (sec < 10 ? '0' : '') + sec;
  }
  function initCountdowns(root) {
    var timers = (root || document).querySelectorAll('[data-countdown]:not(.is-bound)');
    timers.forEach(function (el) {
      el.classList.add('is-bound');
      var remaining = parseInt(el.getAttribute('data-duration'), 10) || 300;
      var display = el.querySelector('[data-countdown-display]') || el;
      display.textContent = fmt(remaining);
      var id = setInterval(function () {
        remaining -= 1;
        if (remaining <= 0) { remaining = 0; clearInterval(id); }
        display.textContent = fmt(remaining);
      }, 1000);
    });
  }

  /* ---------- Copy code ---------- */
  function initCopy(root) {
    var btns = (root || document).querySelectorAll('[data-copy-code]:not(.is-bound)');
    btns.forEach(function (btn) {
      btn.classList.add('is-bound');
      btn.addEventListener('click', function () {
        var code = btn.getAttribute('data-copy-code');
        var label = btn.getAttribute('data-copied-label') || 'Копирано!';
        var original = btn.getAttribute('data-original-label') || btn.textContent;
        btn.setAttribute('data-original-label', original);
        var done = function () {
          btn.textContent = label;
          setTimeout(function () { btn.textContent = original; }, 2000);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(code).then(done).catch(done);
        } else {
          var ta = document.createElement('textarea');
          ta.value = code; document.body.appendChild(ta); ta.select();
          try { document.execCommand('copy'); } catch (e) {}
          document.body.removeChild(ta); done();
        }
      });
    });
  }

  /* ---------- Scroll to top ---------- */
  function initScrollTop() {
    var btn = document.querySelector('.brightly-scroll-top');
    if (!btn || btn.classList.contains('is-bound')) return;
    btn.classList.add('is-bound');
    var onScroll = function () {
      btn.classList.toggle('is-visible', window.pageYOffset > 400);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    btn.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: prefersReduced ? 'auto' : 'smooth' });
    });
  }

  /* ---------- Promo popup ---------- */
  var PROMO_KEY = 'brightly_promo_dismissed';
  function promoDismissed() {
    try {
      var ts = parseInt(localStorage.getItem(PROMO_KEY), 10);
      if (!ts) return false;
      return Date.now() - ts < 30 * 24 * 60 * 60 * 1000;
    } catch (e) { return false; }
  }
  function dismissPromo(popup) {
    popup.classList.remove('is-open');
    document.documentElement.classList.remove('promo-open');
    try { localStorage.setItem(PROMO_KEY, String(Date.now())); } catch (e) {}
  }
  function initPromo() {
    var popup = document.querySelector('[data-promo-popup]');
    if (!popup || popup.classList.contains('is-bound')) return;
    popup.classList.add('is-bound');
    var delay = (parseFloat(popup.getAttribute('data-delay')) || 5) * 1000;
    var inEditor = window.Shopify && window.Shopify.designMode;
    if (!promoDismissed() || inEditor) {
      setTimeout(function () {
        popup.classList.add('is-open');
        document.documentElement.classList.add('promo-open');
        initCountdowns(popup);
      }, inEditor ? 200 : delay);
    }
    popup.querySelectorAll('[data-promo-close]').forEach(function (b) {
      b.addEventListener('click', function () { dismissPromo(popup); });
    });
    var overlay = popup.querySelector('[data-promo-overlay]');
    if (overlay) overlay.addEventListener('click', function () { dismissPromo(popup); });
    var form = popup.querySelector('[data-promo-form]');
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var success = popup.querySelector('[data-promo-success]');
        var initial = popup.querySelector('[data-promo-initial]');
        if (initial) initial.hidden = true;
        if (success) { success.hidden = false; initCopy(success); }
      });
    }
  }

  /* ---------- Bundle selector ---------- */
  function initBundles(root) {
    var groups = (root || document).querySelectorAll('[data-bundle]:not(.is-bound)');
    groups.forEach(function (group) {
      group.classList.add('is-bound');
      var options = group.querySelectorAll('.bundle-option');
      var atcPrice = document.querySelector('[data-bundle-atc-price]');
      var hidden = document.querySelector('[data-bundle-variant-input]');
      function select(opt) {
        options.forEach(function (o) {
          o.classList.toggle('is-selected', o === opt);
          var input = o.querySelector('input[type="radio"]');
          if (input) input.checked = o === opt;
        });
        if (atcPrice && opt.getAttribute('data-price')) {
          atcPrice.textContent = opt.getAttribute('data-price');
        }
        if (hidden && opt.getAttribute('data-variant-id')) {
          hidden.value = opt.getAttribute('data-variant-id');
        }
      }
      options.forEach(function (opt) {
        opt.addEventListener('click', function () { select(opt); });
        opt.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); select(opt); }
        });
      });
      var preset = group.querySelector('.bundle-option.is-selected') || options[0];
      if (preset) select(preset);
    });
  }

  /* ---------- Sticky ATC ---------- */
  function initStickyAtc() {
    var bar = document.querySelector('.brightly-sticky-atc');
    var trigger = document.querySelector('[data-sticky-atc-trigger]');
    if (!bar || bar.classList.contains('is-bound')) return;
    bar.classList.add('is-bound');
    if (!trigger || !('IntersectionObserver' in window)) {
      var onScroll = function () { bar.classList.toggle('is-visible', window.pageYOffset > 700); };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
        });
      },
      { threshold: 0 }
    );
    io.observe(trigger);
  }

  /* ---------- Cart upsell toggle (best-effort add/remove) ---------- */
  function initCartUpsell(root) {
    var toggles = (root || document).querySelectorAll('[data-upsell-toggle]:not(.is-bound)');
    toggles.forEach(function (t) {
      t.classList.add('is-bound');
      t.addEventListener('change', function () {
        var id = t.getAttribute('data-variant-id');
        if (!id) return;
        if (t.checked) {
          fetch((window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add') + '.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: parseInt(id, 10), quantity: 1 }] })
          }).then(function () { window.location.reload(); });
        }
      });
    });
  }

  /* ---------- Bundle add-to-cart (real) ---------- */
  function initBundleAtc(root) {
    var btns = (root || document).querySelectorAll('[data-bundle-atc]:not(.is-atc-bound)');
    btns.forEach(function (btn) {
      btn.classList.add('is-atc-bound');
      btn.addEventListener('click', function () {
        var id = btn.getAttribute('data-variant-id');
        if (!id) { window.location.href = '/cart'; return; }
        var group = btn.closest('.brightly-bundle');
        var sel = group ? group.querySelector('.bundle-option.is-selected') : null;
        var qty = sel && sel.getAttribute('data-qty') ? parseInt(sel.getAttribute('data-qty'), 10) : 1;
        var drawer = document.querySelector('cart-drawer');
        var body = { items: [{ id: parseInt(id, 10), quantity: qty }] };
        if (drawer) { body.sections = ['cart-drawer', 'cart-icon-bubble']; body.sections_url = window.location.pathname; }
        btn.setAttribute('disabled', 'disabled');
        var addUrl = (window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add') + '.js';
        fetch(addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body)
        }).then(function (r) { return r.json(); }).then(function (state) {
          btn.removeAttribute('disabled');
          if (drawer && typeof drawer.renderContents === 'function') {
            drawer.classList.remove('is-empty');
            drawer.renderContents(state);
            if (typeof drawer.open === 'function') drawer.open();
          } else {
            window.location.href = '/cart';
          }
          refreshCart();
          if (drawer) initCountdowns(drawer);
        }).catch(function () { btn.removeAttribute('disabled'); window.location.href = '/cart'; });
      });
    });
  }

  /* ---------- Cart progress bar (free-ship / 10% / gift) ---------- */
  function fmtMoney(cents) {
    return '€' + (cents / 100).toFixed(2).replace('.', ',');
  }
  function updateCartProgress(total) {
    var box = document.querySelector('[data-cart-progress]');
    if (!box) return;
    var cp1 = parseInt(box.getAttribute('data-cp1'), 10) || 4000;
    var cp2 = parseInt(box.getAttribute('data-cp2'), 10) || 6000;
    var cp3 = parseInt(box.getAttribute('data-cp3'), 10) || 8000;
    // Checkpoints are evenly spaced (centres at 16.667% / 50% / 83.333%).
    // Fill segment-by-segment so the bar lines up with the reward it's heading toward.
    var p1 = 100 / 6, p2 = 50, p3 = 500 / 6;
    var pct;
    if (total <= 0) pct = 0;
    else if (total <= cp1) pct = (total / cp1) * p1;
    else if (total <= cp2) pct = p1 + ((total - cp1) / (cp2 - cp1)) * (p2 - p1);
    else if (total <= cp3) pct = p2 + ((total - cp2) / (cp3 - cp2)) * (p3 - p2);
    else pct = 100;
    var fill = box.querySelector('[data-cart-fill]');
    if (fill) fill.style.width = pct + '%';
    var msg = box.querySelector('[data-cart-msg]');
    if (msg) {
      if (total < cp1) msg.innerHTML = 'Трябват ти още <strong>' + fmtMoney(cp1 - total) + '</strong> за <strong>Безплатна доставка</strong>!';
      else if (total < cp2) msg.innerHTML = 'Имаш безплатна доставка! Още <strong>' + fmtMoney(cp2 - total) + '</strong> за <strong>10% отстъпка</strong>.';
      else if (total < cp3) msg.innerHTML = 'Още <strong>' + fmtMoney(cp3 - total) + '</strong> за <strong>Безплатен подарък</strong> 🎁';
      else msg.innerHTML = 'Поздравления! Ти получи всички намаления 🎉';
    }
    box.querySelectorAll('[data-cp]').forEach(function (c) {
      c.classList.toggle('is-reached', total >= (parseInt(c.getAttribute('data-cp'), 10) || 0));
    });
  }
  function refreshCart() {
    if (!document.querySelector('[data-cart-progress]')) return;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) { updateCartProgress(c.total_price); })
      .catch(function () {});
  }
  function cartChanged() {
    var d = document.querySelector('cart-drawer');
    if (d) initCountdowns(d);
    refreshCart();
  }
  function initCart() {
    refreshCart();
    // 1) Dawn pub/sub (global lexical bindings, not window props)
    try {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, cartChanged);
      }
    } catch (e) {}
    // 2) Watch the cart-count bubble (changes on every cart mutation)
    var bubble = document.querySelector('#cart-icon-bubble');
    if (bubble && !bubble.__bfBound && 'MutationObserver' in window) {
      bubble.__bfBound = true;
      new MutationObserver(cartChanged).observe(bubble, { childList: true, subtree: true, characterData: true });
    }
    // 3) Universal: intercept every /cart/* request (Dawn's adds, qty changes, and ours)
    if (!window.__bfCartFetch && window.fetch) {
      window.__bfCartFetch = true;
      var origFetch = window.fetch;
      window.fetch = function () {
        var a0 = arguments[0];
        var url = (a0 && typeof a0 === 'object' && a0.url) ? a0.url : String(a0 || '');
        var ret = origFetch.apply(this, arguments);
        if (/\/cart\/(add|change|update|clear)/.test(url)) {
          ret.then(function () { setTimeout(cartChanged, 150); }).catch(function () {});
        }
        return ret;
      };
    }
    // 4) Foolproof: while the drawer is OPEN, poll the cart and keep the bar in sync
    var cd = document.querySelector('cart-drawer');
    if (cd && !cd.__bfOpenBound && 'MutationObserver' in window) {
      cd.__bfOpenBound = true;
      var pollId = null;
      var sync = function () {
        var open = cd.classList.contains('active') || cd.classList.contains('animate') || cd.hasAttribute('open');
        if (open && pollId === null) {
          cartChanged();
          pollId = setInterval(cartChanged, 800);
        } else if (!open && pollId !== null) {
          clearInterval(pollId);
          pollId = null;
        }
      };
      new MutationObserver(sync).observe(cd, { attributes: true, attributeFilter: ['class', 'open'] });
      sync();
    }
  }

  /* ---------- Boot ---------- */
  /* ---------- Interaction guards: no right-click / image-drag / text-select ---------- */
  function initInteractionGuards() {
    if (window.__bfGuards) return;
    window.__bfGuards = true;
    var isField = function (el) {
      return el && el.closest && el.closest('input, textarea, select, [contenteditable="true"]');
    };
    // Block context menu (right-click / long-press) except on form fields
    document.addEventListener('contextmenu', function (e) {
      if (!isField(e.target)) e.preventDefault();
    });
    // Block image / element dragging
    document.addEventListener('dragstart', function (e) { e.preventDefault(); });
    // Block text selection starting outside form fields
    document.addEventListener('selectstart', function (e) {
      if (!isField(e.target)) e.preventDefault();
    });
  }

  function initAll(root) {
    initInteractionGuards();
    initReveal(root);
    initRings(root);
    initCountdowns(root);
    initCopy(root);
    initBundles(root);
    initBundleAtc(root);
    initCartUpsell(root);
    initScrollTop();
    initStickyAtc();
    initPromo();
    initCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  // Theme editor: re-init when a section is (re)loaded.
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
